import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import {
  Activity,
  ArrowRight,
  ArrowUpRight,
  Bell,
  CalendarDays,
  CarFront,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Hotel,
  KeyRound,
  BarChart3,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  LocateFixed,
  MapPin,
  Pencil,
  PlaneLanding,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Star,
  TriangleAlert,
  Truck,
  UserCheck,
  Users,
  WalletCards,
} from 'lucide-react';
import {
  AddressBook as FeatureContactsIcon,
  CalendarDots as FeatureItineraryIcon,
  Camera as FeaturePhotosIcon,
  Checks as FeatureTasksIcon,
  Confetti as FeatureActivitiesIcon,
  CurrencyInr as FeatureCurrencyIcon,
  EnvelopeSimple as FeatureInviteIcon,
  Gift as FeatureRegistryIcon,
  Handshake as FeatureHandshakeIcon,
  Heart as FeatureHeartIcon,
  InstagramLogo as FeatureInstagramIcon,
  Lamp as FeatureLampIcon,
  MagicWand as FeatureSocialIcon,
  MapPin as FeatureLocationIcon,
  Palette as FeatureThemeIcon,
  Sparkle as FeatureSparkleIcon,
  Star as FeatureStarIcon,
  Storefront as FeatureVendorsIcon,
  UsersThree as FeatureGuestsIcon,
  Wallet as FeatureBudgetIcon,
} from '@phosphor-icons/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { EventAwareDateRangePicker } from '../../components/EventAwareDatePicker';
import EventReadinessPanel from '../../components/EventReadinessPanel';
import VenueSelector from '../../components/VenueSelector';
import RoomBedIcon from '../../components/icons/RoomBedIcon';
import InfoTip from '../../components/InfoTip';
import { fallbackDashboardReadiness, getEventReadinessItems } from '../../lib/eventReadiness';
import { isWithinEventPeriod } from '../../lib/eventPeriod';
import useEventTypeConfig from '../../hooks/useEventTypeConfig';
import { formatEventNameList, getEventTypeConfig } from '../../config/eventTypes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import './EventManagerDashboard.css';

const priorityIcons = {
  transport: PlaneLanding,
  accommodation: Hotel,
  keys: KeyRound,
  rsvp: UserCheck,
  tasks: ListChecks,
  budget: WalletCards,
};

const commandItems = [
  { label: 'Overview', detail: 'Event operations dashboard', to: '/dashboard', icon: LayoutDashboard },
  { label: 'Guests', detail: 'RSVPs, invitations and profiles', to: '/dashboard/guests', icon: Users },
  { label: 'Rooms', detail: 'Allocation, arrivals and hotel operations', to: '/dashboard/rooms', icon: RoomBedIcon },
  { label: 'Itinerary', detail: 'Event schedule and venues', to: '/dashboard/itinerary', icon: CalendarDays },
  { label: 'Tasks', detail: 'Assignments and operational work', to: '/dashboard/tasks', icon: ListChecks },
  { label: 'Vendors', detail: 'Partners and event suppliers', to: '/dashboard/vendors', icon: Truck },
  { label: 'Budget', detail: 'Costs, team expenses, payments and reports', to: '/dashboard/budget', icon: WalletCards },
  { label: 'Settings', detail: 'Event and account configuration', to: '/dashboard/settings', icon: Settings },
];

const commandCenterPreferenceKey = (userId) => `galago:command-center:${userId}`;

const dashboardFeatureItems = [
  { label: 'Guests', to: '/dashboard/guests', icon: FeatureGuestsIcon, accentIcon: FeatureHeartIcon },
  { label: 'Itinerary', to: '/dashboard/itinerary', icon: FeatureItineraryIcon, accentIcon: FeatureLocationIcon, featureKey: 'showItinerary' },
  { label: 'Rooms', to: '/dashboard/rooms', icon: RoomBedIcon, accentIcon: FeatureLampIcon, featureKey: 'showAccommodation' },
  { label: 'Tasks', to: '/dashboard/tasks', icon: FeatureTasksIcon },
  { label: 'Registry', to: '/dashboard/gifts', icon: FeatureRegistryIcon, accentIcon: FeatureHeartIcon, featureKey: 'showWishlist' },
  { label: 'Vendors', to: '/dashboard/vendors', icon: FeatureVendorsIcon, accentIcon: FeatureHandshakeIcon },
  { label: 'Budget', to: '/dashboard/budget', icon: FeatureBudgetIcon, accentIcon: FeatureCurrencyIcon },
  { label: 'Key contacts', to: '/dashboard/contacts', icon: FeatureContactsIcon, accentIcon: FeatureStarIcon },
  { label: 'Photos', to: '/dashboard/photos', icon: FeaturePhotosIcon, accentIcon: FeatureSparkleIcon },
  { label: 'Social Studio', to: '/dashboard/social', icon: FeatureSocialIcon, accentIcon: FeatureInstagramIcon, featureKey: 'showSocialStudio' },
  { label: 'Invite Studio', to: '/dashboard/theme', icon: FeatureThemeIcon, accentIcon: FeatureInviteIcon, featureKey: 'showInviteStudio' },
  { label: 'Celebration Activities', to: '/dashboard/games', icon: FeatureActivitiesIcon, accentIcon: FeatureSparkleIcon, featureKey: 'showCelebrationGames' },
  { label: 'Web Invite', to: '/dashboard/e-invite', icon: FeatureInviteIcon, accentIcon: FeatureSparkleIcon, featureKey: 'showWebInvite' },
];

const defaultEventCovers = {
  wedding: '/assets/event-covers/dashboard-header-wedding-v2.png',
  corporate: '/assets/event-covers/corporate.jpg',
  baby_shower: '/assets/event-covers/baby-shower.jpg',
  birthday: '/assets/event-covers/birthday.jpg',
};

const defaultNextUpCovers = {
  wedding: '/assets/event-covers/dashboard-next-up-wedding-v2.png',
};

const dashboardTaglines = {
  wedding: 'Your wedding, your way.',
  corporate: 'Every detail, thoughtfully coordinated.',
  baby_shower: 'A joyful welcome, beautifully planned.',
  birthday: 'A celebration made uniquely yours.',
};

const nextStepCopy = {
  event_details: { title: 'Complete your event details', action: 'Edit event' },
  itinerary: { title: 'Add your itinerary', action: 'Create itinerary' },
  guests: { title: 'Build your guest list', action: 'Add guests' },
  web_invite: { title: 'Create your web invite', action: 'Open Web Invite' },
};

const getDashboardCover = (event) => {
  return defaultEventCovers[event?.event_type] || defaultEventCovers.wedding;
};

const dateInputValue = (value) => (value ? format(new Date(value), 'yyyy-MM-dd') : '');

const formatEventRange = (startDate, endDate) => {
  if (!startDate) return 'Dates not set';
  const start = new Date(startDate);
  if (!endDate) return format(start, 'd MMMM yyyy');
  const end = new Date(endDate);
  if (start.getFullYear() === end.getFullYear()) {
    return `${format(start, 'd MMM')} - ${format(end, 'd MMM yyyy')}`;
  }
  return `${format(start, 'd MMM yyyy')} - ${format(end, 'd MMM yyyy')}`;
};

const formatTime = (value) => {
  if (!value) return 'Not scheduled';
  return format(new Date(value), 'HH:mm');
};

const formatRelativeAction = (value) => value || 'Review';
const formatBudgetMoney = (value, currency = 'INR') => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(Number(value || 0));
  } catch (_error) {
    return `${currency} ${Number(value || 0).toLocaleString('en-IN')}`;
  }
};

const liveLocationWindowOptions = [
  { value: 'event_day', label: 'Until event day ends' },
  { value: 'one_hour', label: 'For 1 hour' },
  { value: 'manual', label: 'Until I stop it' },
];

const getEventDayShareUntil = (event) => {
  const dateValue = event?.end_date || event?.start_date;
  if (!dateValue) return null;
  const eventEnd = new Date(dateValue);
  if (Number.isNaN(eventEnd.getTime())) return null;
  eventEnd.setHours(23, 59, 59, 999);
  return eventEnd.toISOString();
};

const getShareUntilForWindow = (event, windowValue) => {
  if (windowValue === 'one_hour') {
    return new Date(Date.now() + 60 * 60 * 1000).toISOString();
  }
  if (windowValue === 'event_day') {
    return getEventDayShareUntil(event);
  }
  return null;
};

const formatLocationTime = (value) => {
  if (!value) return 'Not updated yet';
  try {
    return format(new Date(value), 'd MMM, h:mm a');
  } catch (_error) {
    return 'Recently updated';
  }
};

const getGeolocationErrorMessage = (error) => {
  if (error?.code === 1) return 'Location permission was blocked. Allow location access in the browser to share it.';
  if (error?.code === 2) return 'Your current location could not be detected. Please try again from the event venue.';
  if (error?.code === 3) return 'Location detection timed out. Please try again.';
  return 'Live location is not available in this browser.';
};

function StatusPill({ children, tone = 'neutral', dot = false }) {
  return (
    <span className={`qc-status qc-status--${tone}`}>
      {dot && <span className="qc-status__dot" />}
      {children}
    </span>
  );
}

function ManagerLiveLocationCard({ event }) {
  const [liveLocation, setLiveLocation] = useState(null);
  const [shareWindow, setShareWindow] = useState('event_day');
  const [busyAction, setBusyAction] = useState('');
  const [locationError, setLocationError] = useState('');
  const lastBackgroundPushRef = useRef(0);
  const eventId = event?.id;
  const isSharing = Boolean(liveLocation?.enabled);

  const loadLiveLocation = useCallback(async () => {
    if (!eventId) return;
    try {
      const response = await api.get(`/events/${eventId}/manager-location`);
      setLiveLocation(response.data);
    } catch (requestError) {
      console.error('Failed to load manager live location:', requestError);
    }
  }, [eventId]);

  useEffect(() => {
    setLiveLocation(null);
    setLocationError('');
    loadLiveLocation();
  }, [loadLiveLocation]);

  const buildLocationPayload = useCallback((position) => ({
    enabled: true,
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    accuracy: position.coords.accuracy ?? null,
    heading: position.coords.heading ?? null,
    speed: position.coords.speed ?? null,
    label: 'Event manager',
    share_until: getShareUntilForWindow(event, shareWindow),
  }), [event, shareWindow]);

  const pushLocation = useCallback(async (position) => {
    if (!eventId) return null;
    const response = await api.put(
      `/events/${eventId}/manager-location`,
      buildLocationPayload(position)
    );
    setLiveLocation(response.data);
    return response.data;
  }, [buildLocationPayload, eventId]);

  const startSharing = () => {
    if (!navigator.geolocation) {
      const message = 'Live location is not available in this browser.';
      setLocationError(message);
      toast.error(message);
      return;
    }

    setBusyAction('start');
    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await pushLocation(position);
          lastBackgroundPushRef.current = Date.now();
          toast.success('Live location sharing is on');
        } catch (requestError) {
          const message = requestError.response?.data?.detail || 'Could not start live location sharing';
          setLocationError(message);
          toast.error(message);
        } finally {
          setBusyAction('');
        }
      },
      (error) => {
        const message = getGeolocationErrorMessage(error);
        setLocationError(message);
        setBusyAction('');
        toast.error(message);
      },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 15000 }
    );
  };

  useEffect(() => {
    if (!eventId || !isSharing || !navigator.geolocation) return undefined;

    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        const now = Date.now();
        if (now - lastBackgroundPushRef.current < 25000) return;
        lastBackgroundPushRef.current = now;
        try {
          await pushLocation(position);
        } catch (requestError) {
          console.error('Failed to refresh manager live location:', requestError);
        }
      },
      (error) => {
        console.error('Live location watch failed:', error);
      },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [eventId, isSharing, pushLocation]);

  const stopSharing = async () => {
    if (!eventId) return;
    try {
      setBusyAction('stop');
      const response = await api.delete(`/events/${eventId}/manager-location`);
      setLiveLocation(response.data);
      toast.success('Live location sharing is off');
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || 'Could not stop live location sharing');
    } finally {
      setBusyAction('');
    }
  };

  return (
    <article className={`qc-panel qc-live-location ${isSharing ? 'is-active' : ''}`}>
      <div className="qc-live-location__main">
        <div className="qc-live-location__icon">
          <LocateFixed size={21} />
        </div>
        <div>
          <div className="qc-live-location__header">
            <span>Day-of support</span>
            <StatusPill tone={isSharing ? 'live' : 'neutral'} dot={isSharing}>
              {isSharing ? 'Sharing live' : 'Private'}
            </StatusPill>
          </div>
          <div className="flex items-center gap-1">
            <h2>Manager live location</h2>
            <InfoTip
              content="Share your current position with guests who need help finding you. Updates continue while this dashboard stays open."
              label="About manager live location"
            />
          </div>
          {isSharing && (
            <div className="qc-live-location__meta">
              <span><Clock3 size={14} /> {formatLocationTime(liveLocation?.updated_at)}</span>
              <span><MapPin size={14} /> Directions enabled</span>
            </div>
          )}
          {locationError && <p className="qc-live-location__error">{locationError}</p>}
        </div>
      </div>

      <div className="qc-live-location__controls">
        <label>
          <span className="sr-only">Share window</span>
          <select
            aria-label="Share window"
            value={shareWindow}
            onChange={(changeEvent) => setShareWindow(changeEvent.target.value)}
            disabled={isSharing || Boolean(busyAction)}
          >
            {liveLocationWindowOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        {isSharing ? (
          <button
            type="button"
            className="qc-button qc-button--light"
            onClick={stopSharing}
            disabled={busyAction === 'stop'}
          >
            {busyAction === 'stop' ? <RefreshCw size={16} className="qc-spin" /> : <LocateFixed size={16} />}
            Stop sharing
          </button>
        ) : (
          <button
            type="button"
            className="qc-button qc-button--gold"
            onClick={startSharing}
            disabled={busyAction === 'start'}
          >
            {busyAction === 'start' ? <RefreshCw size={16} className="qc-spin" /> : <LocateFixed size={16} />}
            Start sharing
          </button>
        )}
      </div>
    </article>
  );
}

function SectionHeader({ eyebrow, title, action, to, icon: Icon }) {
  return (
    <div className="qc-section-header">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={18} aria-hidden="true" />}
        <p className="sr-only">{eyebrow}</p>
        <h2>{title}</h2>
      </div>
      {action && to && (
        <Link to={to} className="qc-text-action">
          {action} <ArrowRight size={16} />
        </Link>
      )}
    </div>
  );
}

function DashboardFeatureGrid({ eventTypeConfig }) {
  const availableFeatures = dashboardFeatureItems.filter(
    (item) => (!item.featureKey || eventTypeConfig.features[item.featureKey])
      && (!item.eventTypes || item.eventTypes.includes(eventTypeConfig.id))
  );

  return (
    <section className="qc-feature-overview" aria-labelledby="dashboard-features-title" data-testid="dashboard-feature-overview">
      <div className="qc-feature-grid">
        {availableFeatures.map(({ label, to, icon: Icon, accentIcon: AccentIcon }) => (
          <Link to={to} className="qc-feature-card" key={to}>
            <span className="qc-feature-card__icon">
              <span className="qc-feature-card__icon-art">
                <Icon size={27} weight="duotone" />
                {AccentIcon && <AccentIcon className="qc-feature-card__accent-icon" size={13} weight="fill" />}
              </span>
            </span>
            <strong>{label}</strong>
            <ChevronRight className="qc-feature-card__arrow" size={17} />
          </Link>
        ))}
      </div>
    </section>
  );
}

function DashboardNextStep({ readiness, eventType, event, coverUrl }) {
  const nextRequiredItem = getEventReadinessItems(readiness, eventType, event)
    .filter((item) => item.required)
    .find((item) => !item.complete);
  const isComplete = !nextRequiredItem;
  const eventFeatures = getEventTypeConfig(eventType).features;
  const completedContent = eventFeatures.showInviteStudio
    ? {
      title: eventFeatures.showSocialStudio
        ? 'Create invites & social content'
        : 'Create and share your invitation',
      action: 'Open Invite Studio',
      detail: eventFeatures.showSocialStudio
        ? 'Design your invitation, then turn celebration moments into ready-to-share social posts.'
        : 'Use your event theme and details to create a polished invitation for your guests.',
      path: '/dashboard/theme',
    }
    : eventFeatures.showSocialStudio
      ? {
        title: 'Create your social content',
        action: 'Open Social Studio',
        detail: 'Turn your event details and photos into polished content ready to share.',
        path: '/dashboard/social',
      }
      : {
        title: 'Your essentials are ready',
        action: 'Manage tasks',
        detail: 'Keep the celebration moving with your next planning task.',
        path: '/dashboard/tasks',
      };
  const content = isComplete
    ? completedContent
    : {
      ...(nextStepCopy[nextRequiredItem.id] || {
        title: nextRequiredItem.title,
        action: `Open ${nextRequiredItem.title}`,
      }),
      detail: nextRequiredItem.detail,
      path: nextRequiredItem.path,
    };

  return (
    <section className="qc-next-up" aria-labelledby="dashboard-next-up-title" data-testid="dashboard-next-up">
      <div className="qc-next-up__media" aria-hidden="true" style={{ backgroundImage: `url(${JSON.stringify(coverUrl)})` }} />
      <div className="qc-next-up__content">
        <span>Next up</span>
        <h2 id="dashboard-next-up-title">{content.title}</h2>
        <p>{content.detail}</p>
        <Link to={content.path} className="qc-next-up__action">
          {content.action} <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
}

function RingChart({ percent, type, label }) {
  const clamped = Math.min(100, Math.max(0, percent || 0));
  const colors = type === 'rooms'
    ? `#527a8d 0 ${clamped}%, #d8e0e2 ${clamped}% 100%`
    : `#327359 0 ${clamped}%, #d5a24a ${clamped}% ${Math.min(100, clamped + 6)}%, #c66b60 ${Math.min(100, clamped + 6)}% 100%`;
  return (
    <div
      className="qc-ring"
      style={{ background: `conic-gradient(${colors})` }}
      role="img"
      aria-label={`${clamped} percent ${label}`}
    >
      <span>
        <strong>{clamped}%</strong>
        <small>{label}</small>
      </span>
    </div>
  );
}

function PriorityCard({ priorities, compact = false }) {
  return (
    <section className={`qc-panel qc-priority ${compact ? 'qc-priority--compact' : ''}`}>
      <SectionHeader eyebrow="Generated from live event data" title="Priorities" action="Open tasks" to="/dashboard/tasks" icon={Activity} />
      <div className="qc-priority__list">
        {priorities.length ? priorities.slice(0, 4).map((item) => {
          const Icon = priorityIcons[item.type] || Clock3;
          return (
            <Link to={item.deep_link} className={`qc-priority__item qc-priority__item--${item.tone}`} key={item.id}>
              <span className="qc-priority__icon"><Icon size={18} /></span>
              <span className="qc-priority__copy">
                <strong>{item.title}</strong>
              </span>
              <span className="qc-priority__deadline">
                <strong>{formatRelativeAction(item.action_by)}</strong>
                <ArrowUpRight size={15} />
              </span>
            </Link>
          );
        }) : (
          <div className="qc-priority__empty">
            <CheckCircle2 size={28} />
            <strong>No immediate decisions</strong>
            <span>Live event signals are clear.</span>
          </div>
        )}
      </div>
    </section>
  );
}

function DashboardSkeleton() {
  return (
    <div className="qc-dashboard qc-dashboard--loading" aria-label="Loading operations dashboard">
      <div className="qc-skeleton qc-skeleton--topbar" />
      <div className="qc-content">
        <div className="qc-skeleton qc-skeleton--hero" />
        <div className="qc-skeleton-grid">
          <div className="qc-skeleton qc-skeleton--card" />
          <div className="qc-skeleton qc-skeleton--card" />
          <div className="qc-skeleton qc-skeleton--card" />
        </div>
        <div className="qc-skeleton qc-skeleton--table" />
      </div>
    </div>
  );
}

export default function EventManagerDashboard() {
  const { currentEvent, refreshCurrentEvent, user } = useAuth();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [commandCenterEnabled, setCommandCenterEnabled] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [savingItinerarySetting, setSavingItinerarySetting] = useState(false);
  const [savingAccommodationSetting, setSavingAccommodationSetting] = useState(false);
  const [eventDraft, setEventDraft] = useState({
    event_type: 'wedding',
    name: '',
    bride_name: '',
    groom_name: '',
    host_names: '',
    honoree_names: '',
    milestone_number: '',
    description: '',
    start_date: '',
    end_date: '',
    venue: {},
  });
  const draftEventTypeConfig = getEventTypeConfig(eventDraft.event_type);

  const eventId = currentEvent?.id;

  useEffect(() => {
    if (!user?.id) {
      setCommandCenterEnabled(false);
      return;
    }

    try {
      setCommandCenterEnabled(
        window.localStorage.getItem(commandCenterPreferenceKey(user.id)) === 'true'
      );
    } catch {
      setCommandCenterEnabled(false);
    }
  }, [user?.id]);

  const handleCommandCenterChange = useCallback((enabled) => {
    setCommandCenterEnabled(enabled);

    if (!user?.id) return;

    try {
      window.localStorage.setItem(commandCenterPreferenceKey(user.id), String(enabled));
    } catch {
      // Keep the in-session preference when browser storage is unavailable.
    }
  }, [user?.id]);

  const loadDashboard = async () => {
    if (!eventId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/events/${eventId}/operations-dashboard`);
      setDashboard(response.data);
    } catch (requestError) {
      console.error('Failed to load operations dashboard:', requestError);
      setError(requestError.response?.data?.detail || 'The operations snapshot could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // Event changes should always fetch a fresh command-center snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (!currentEvent) return;
    setEventDraft({
      event_type: currentEvent.event_type || 'wedding',
      name: currentEvent.name || '',
      bride_name: currentEvent.bride_name || '',
      groom_name: currentEvent.groom_name || '',
      host_names: (currentEvent.host_names || []).join(', '),
      honoree_names: formatEventNameList(currentEvent.honoree_names, ', '),
      milestone_number: currentEvent.milestone_number || '',
      description: currentEvent.description || '',
      start_date: dateInputValue(currentEvent.start_date),
      end_date: dateInputValue(currentEvent.end_date),
      venue: currentEvent.venues?.[0] || {},
    });
  }, [currentEvent]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') !== 'edit-event') return undefined;

    setEditOpen(true);
    const clearUrlTimer = window.setTimeout(() => {
      navigate('/dashboard', { replace: true });
    }, 0);

    return () => window.clearTimeout(clearUrlTimer);
  }, [location.search, navigate]);

  useEffect(() => {
    const openSearch = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', openSearch);
    return () => window.removeEventListener('keydown', openSearch);
  }, []);

  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const availableItems = commandItems.filter((item) => (
      (item.to !== '/dashboard/rooms' || eventTypeConfig.features.showAccommodation)
      && (item.to !== '/dashboard/itinerary' || eventTypeConfig.features.showItinerary)
    ));
    if (!query) return availableItems;
    return availableItems.filter((item) => (
      item.label.toLowerCase().includes(query)
      || item.detail.toLowerCase().includes(query)
    ));
  }, [
    eventTypeConfig.features.showAccommodation,
    eventTypeConfig.features.showItinerary,
    searchQuery,
  ]);

  const updateItinerarySetting = async (enabled) => {
    if (!eventId || savingItinerarySetting) return;
    try {
      setSavingItinerarySetting(true);
      await api.put(`/events/${eventId}/itinerary-settings`, { enabled });
      await refreshCurrentEvent();
      toast.success(enabled ? 'Itinerary management enabled' : 'Itinerary management hidden');
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || 'Could not update itinerary settings');
    } finally {
      setSavingItinerarySetting(false);
    }
  };

  const updateAccommodationSetting = async (enabled) => {
    if (!eventId || savingAccommodationSetting) return;
    try {
      setSavingAccommodationSetting(true);
      await api.put(`/events/${eventId}/accommodation-settings`, { enabled });
      await refreshCurrentEvent();
      await loadDashboard();
      toast.success(enabled ? 'Accommodation management enabled' : 'Accommodation management hidden');
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || 'Could not update accommodation settings');
    } finally {
      setSavingAccommodationSetting(false);
    }
  };

  const displayEventName = eventTypeConfig.isWedding
    ? [currentEvent?.bride_name, currentEvent?.groom_name].filter(Boolean).join(' & ')
      || dashboard?.event?.name || currentEvent?.name
    : eventTypeConfig.isBirthday
      ? formatEventNameList(currentEvent?.honoree_names) || dashboard?.event?.name || currentEvent?.name
    : (currentEvent?.host_names || []).join(' & ') || dashboard?.event?.name || currentEvent?.name;

  const saveEvent = async (event) => {
    event.preventDefault();
    if (!eventDraft.name.trim() || !eventDraft.start_date) {
      toast.error('Event name and start date are required');
      return;
    }
    if (eventDraft.event_type === 'wedding' && (!eventDraft.bride_name.trim() || !eventDraft.groom_name.trim())) {
      toast.error('Bride name and groom name are required for wedding events');
      return;
    }
    if (eventDraft.event_type === 'birthday' && !eventDraft.honoree_names.trim()) {
      toast.error('Add at least one birthday celebrant');
      return;
    }
    if (eventDraft.end_date && eventDraft.end_date < eventDraft.start_date) {
      toast.error('End date cannot be earlier than start date');
      return;
    }
    try {
      setSavingEvent(true);
      await api.put(`/events/${eventId}`, {
        name: eventDraft.name.trim(),
        description: eventDraft.description.trim() || null,
        start_date: new Date(eventDraft.start_date).toISOString(),
        end_date: eventDraft.end_date ? new Date(eventDraft.end_date).toISOString() : null,
        event_type: eventDraft.event_type,
        branding: currentEvent.branding || {},
        bride_name: eventDraft.event_type === 'wedding'
          ? eventDraft.bride_name.trim() || null
          : null,
        groom_name: eventDraft.event_type === 'wedding'
          ? eventDraft.groom_name.trim() || null
          : null,
        host_names: eventDraft.event_type !== 'wedding'
          ? eventDraft.host_names.split(',').map((name) => name.trim()).filter(Boolean)
          : [],
        honoree_names: eventDraft.event_type === 'birthday'
          ? eventDraft.honoree_names.split(',').map((name) => name.trim()).filter(Boolean)
          : [],
        milestone_number: eventDraft.event_type === 'birthday' && eventDraft.milestone_number
          ? Number(eventDraft.milestone_number)
          : null,
        venues: eventDraft.event_type === 'birthday'
          && (eventDraft.venue?.name?.trim() || eventDraft.venue?.address?.trim())
          ? [eventDraft.venue]
          : [],
      });
      await refreshCurrentEvent();
      await loadDashboard();
      setEditOpen(false);
      toast.success('Event details updated');
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || 'Failed to update event');
    } finally {
      setSavingEvent(false);
    }
  };

  if (!currentEvent) {
    return (
      <DashboardLayout variant="command">
        <div className="qc-no-event">
          <LayoutDashboard size={30} />
          <h1>Create your first event</h1>
          <p>Your event dashboard will appear here.</p>
          <Link to="/dashboard/events/new" className="qc-button qc-button--gold">
            <Plus size={17} /> Create event
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout variant="command">
        <DashboardSkeleton />
      </DashboardLayout>
    );
  }

  if (error || !dashboard) {
    return (
      <DashboardLayout variant="command">
        <div className="qc-error-state">
          <TriangleAlert size={28} />
          <h1>Dashboard unavailable</h1>
          <p>{error}</p>
          <Button type="button" onClick={loadDashboard}>
            <RefreshCw className="mr-2 h-4 w-4" /> Try again
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const { rsvp, rooms, tasks, transport, priorities, health, budget } = dashboard;
  const visiblePriorities = priorities.filter((item) => (
    (eventTypeConfig.features.showAccommodation || !['accommodation', 'keys'].includes(item.type))
    && (eventTypeConfig.features.showTravelAndId || item.type !== 'transport')
  ));
  const totalPriorityCount = visiblePriorities.length;
  const arrivalGuestCount = transport.upcoming_arrivals.reduce(
    (total, arrival) => total + arrival.party_size,
    0
  );
  const invitedGuests = rsvp.invited ?? rsvp.total;
  const allocatedGuestCount = rooms.allocated_guests ?? rooms.allocated;
  const guestsRequiringRooms = rooms.guests_requiring_rooms ?? invitedGuests;
  const guestsPendingAllocation = rooms.guests_pending_allocation
    ?? Math.max(guestsRequiringRooms - allocatedGuestCount, 0);
  const eventLocation = dashboard.event.location || 'Event operations';
  const readiness = dashboard.readiness
    || fallbackDashboardReadiness(currentEvent, dashboard);
  const dashboardCover = getDashboardCover(currentEvent);
  const nextUpCover = defaultNextUpCovers[currentEvent.event_type] || dashboardCover;
  const dashboardTagline = dashboardTaglines[currentEvent.event_type]
    || eventTypeConfig.labels.appTagline;
  const dashboardWelcomeName = user?.name?.trim() || displayEventName;

  return (
    <DashboardLayout variant="command">
      <div className="qc-dashboard">
        {/* <header className="qc-topbar">
          <div className="qc-topbar__context">
            <strong>Event overview</strong>
            <span>
              {format(new Date(), 'EEEE, d MMMM')} - Day {dashboard.event.day_number} of {dashboard.event.total_days}
            </span>
          </div>
          <div className="qc-topbar__tools">
            <button type="button" className="qc-search-trigger" onClick={() => setSearchOpen(true)}>
              <Search size={17} />
              <span>Search anything</span>
              <kbd>Ctrl K</kbd>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="qc-icon-button qc-notification-button" aria-label="Notifications" title="Notifications">
                  <Bell size={18} />
                  {totalPriorityCount > 0 && <span />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Operations requiring attention</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {priorities.length ? priorities.slice(0, 4).map((item) => (
                  <DropdownMenuItem key={item.id} onClick={() => navigate(item.deep_link)} className="items-start py-3">
                    <span className="min-w-0">
                      <strong className="block truncate text-sm">{item.title}</strong>
                      <small className="mt-1 block text-muted-foreground">{item.action_by}</small>
                    </span>
                  </DropdownMenuItem>
                )) : (
                  <DropdownMenuItem disabled>No urgent items</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="qc-user-avatar" title={user?.name || 'Account'}>
              {(user?.name || 'U').slice(0, 2).toUpperCase()}
            </span>
          </div>
        </header> */}

        <main className="qc-content">
          <section className="qc-hero">
            <div className="qc-hero__media" aria-hidden="true" style={{ backgroundImage: `url(${JSON.stringify(dashboardCover)})` }} />
            <div className="qc-hero__copy">
              <p className="qc-hero__eyebrow">Welcome back,</p>
              <h1>{dashboardWelcomeName}</h1>
              <p className="qc-hero__tagline">{dashboardTagline}</p>
              <div className="qc-hero__meta">
                <span>{formatEventRange(dashboard.event.start_date, dashboard.event.end_date)}</span>
              </div>
            </div>
            <div className="qc-hero__command">
              <div className="qc-hero__actions">
                <button
                  type="button"
                  className="qc-icon-button qc-icon-button--inverse"
                  onClick={() => setEditOpen(true)}
                  aria-label="Edit event details"
                  title="Edit event details"
                  data-testid="edit-event-button"
                >
                  <Pencil size={17} />
                </button>
              </div>
            </div>
          </section>

          <EventReadinessPanel
            readiness={readiness}
            eventType={currentEvent.event_type}
            event={currentEvent}
            compact
            showChecklist
          />

          <div className="qc-workspace-heading">
            <h2>{commandCenterEnabled ? 'Dashboard' : 'Quick Access'}</h2>
            <div className="qc-dashboard-mode" role="tablist" aria-label="Workspace view">
              <button
                type="button"
                role="tab"
                aria-selected={!commandCenterEnabled}
                aria-label="Quick Access"
                title="Quick Access"
                className={!commandCenterEnabled ? 'is-active' : ''}
                onClick={() => handleCommandCenterChange(false)}
                data-testid="quick-access-tab"
              >
                <LayoutGrid size={18} />
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={commandCenterEnabled}
                aria-label="Dashboard"
                title="Dashboard"
                className={commandCenterEnabled ? 'is-active' : ''}
                onClick={() => handleCommandCenterChange(true)}
                data-testid="command-center-toggle"
              >
                <BarChart3 size={18} />
              </button>
            </div>
          </div>

          {!commandCenterEnabled && (
            <>
              <DashboardFeatureGrid eventTypeConfig={eventTypeConfig} />
              <DashboardNextStep
                readiness={readiness}
                eventType={currentEvent.event_type}
                event={currentEvent}
                coverUrl={nextUpCover}
              />
            </>
          )}

          {commandCenterEnabled && (
            <>

          {eventTypeConfig.features.itineraryOptional && (
            <section className="qc-panel flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="itinerary-setting-card">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                  <CalendarDays size={20} />
                </span>
                <div>
                  <h2 className="font-semibold text-slate-900">Maintain a detailed itinerary</h2>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
                    Keep this off for a single-session or half-day event. Enable it when you need multiple schedules, venues, or guest-specific event assignments.
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3 rounded-xl border bg-white px-4 py-3">
                <span className="text-sm font-medium text-slate-700">
                  {eventTypeConfig.features.showItinerary ? 'Enabled' : 'Not needed'}
                </span>
                <Switch
                  checked={eventTypeConfig.features.showItinerary}
                  onCheckedChange={updateItinerarySetting}
                  disabled={savingItinerarySetting}
                  aria-label="Maintain a detailed itinerary"
                  data-testid="itinerary-setting-toggle"
                />
              </div>
            </section>
          )}

          {eventTypeConfig.features.accommodationOptional && (
            <section className="qc-panel flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="accommodation-setting-card">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                  <Hotel size={20} />
                </span>
                <div>
                  <h2 className="font-semibold text-slate-900">Manage guest accommodation</h2>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
                    Enable this only when guests need rooms, stay preferences, or room assignments for this celebration.
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3 rounded-xl border bg-white px-4 py-3">
                <span className="text-sm font-medium text-slate-700">
                  {eventTypeConfig.features.showAccommodation ? 'Enabled' : 'Not needed'}
                </span>
                <Switch
                  checked={eventTypeConfig.features.showAccommodation}
                  onCheckedChange={updateAccommodationSetting}
                  disabled={savingAccommodationSetting}
                  aria-label="Manage guest accommodation"
                  data-testid="accommodation-setting-toggle"
                />
              </div>
            </section>
          )}

          {eventTypeConfig.features.showManagerLocation
            && isWithinEventPeriod(currentEvent)
            && <ManagerLiveLocationCard event={currentEvent} />}

          <section className="qc-position-grid" aria-label="Event position at a glance">
            <article className="qc-panel qc-position-card">
              <SectionHeader eyebrow="Guest response" title="RSVP" action="Open" to="/dashboard/guests" icon={UserCheck} />
              <div className="qc-position-card__body">
                <RingChart percent={rsvp.confirmed_percent} type="rsvp" label="confirmed" />
                <div className="qc-stat-list">
                  <Link to="/dashboard/guests">
                    <span><i className="is-ink" />Total invited</span><strong>{invitedGuests}</strong>
                  </Link>
                  <Link to="/dashboard/guests?rsvp=confirmed">
                    <span><i className="is-green" />Confirmed</span><strong>{rsvp.confirmed}</strong>
                  </Link>
                  <Link to="/dashboard/guests?rsvp=pending">
                    <span><i className="is-gold" />Pending</span><strong>{rsvp.pending}</strong>
                  </Link>
                  <Link to="/dashboard/guests?rsvp=declined">
                    <span><i className="is-red" />Declined</span><strong>{rsvp.declined}</strong>
                  </Link>
                </div>
              </div>
              {rsvp.pending > 0 && <footer><UserCheck size={15} /> {rsvp.pending} need follow-up</footer>}
            </article>

            <article className="qc-panel qc-position-card qc-budget-card">
              <SectionHeader eyebrow="Commercial" title="Budget" action={budget?.configured ? 'Open' : 'Set up'} to="/dashboard/budget" icon={WalletCards} />
              {budget?.configured ? (
                <>
                  <div className="qc-budget-live">
                    <div>
                      <span>Spent till date</span>
                      <strong>{formatBudgetMoney(budget.forecast, budget.currency)}</strong>
                      <small>of {formatBudgetMoney(budget.total_budget ?? budget.approved_budget, budget.currency)} total budget</small>
                    </div>
                    <div className="qc-budget-live__stats">
                      <span><small>Committed</small><strong>{formatBudgetMoney(budget.committed, budget.currency)}</strong></span>
                      <span><small>Paid</small><strong>{formatBudgetMoney(budget.paid, budget.currency)}</strong></span>
                      <span className={budget.overrun > 0 ? 'is-risk' : ''}><small>{budget.overrun > 0 ? 'Overrun' : 'Available'}</small><strong>{formatBudgetMoney(budget.overrun > 0 ? budget.overrun : budget.available, budget.currency)}</strong></span>
                    </div>
                  </div>
                  <div className="qc-budget-placeholder" aria-label={`${budget.utilization_percent}% of budget forecast`}>
                    <span style={{ width: `${Math.min(100, budget.utilization_percent || 0)}%` }} />
                  </div>
                  {budget.awaiting_approval > 0 && <footer><CircleDollarSign size={15} /> {budget.awaiting_approval} approvals</footer>}
                </>
              ) : (
                <div className="qc-budget-empty">
                  <span><WalletCards size={25} /></span>
                  <div>
                    <strong>Budget tracking is not configured</strong>
                    <p>Generate category allocations and approval rules from one total event budget.</p>
                  </div>
                </div>
              )}
            </article>

            {eventTypeConfig.features.showAccommodation && <article className="qc-panel qc-position-card">
              <SectionHeader eyebrow="Accommodation" title="Rooms" action="Open" to="/dashboard/rooms" icon={RoomBedIcon} />
              <div className="qc-position-card__body">
                <RingChart percent={rooms.allocated_percent} type="rooms" label="guests placed" />
                <div className="qc-stat-list qc-stat-list--rooms">
                  <Link to="/dashboard/rooms">
                    <span>Guests with rooms</span><strong>{allocatedGuestCount}</strong>
                  </Link>
                  <Link to="/dashboard/rooms?tab=rooms" className={guestsPendingAllocation ? 'is-alert' : ''}>
                    <span>Need room allocation</span><strong>{guestsPendingAllocation}</strong>
                  </Link>
                  <Link to="/dashboard/rooms">
                    <span>Beds available</span><strong>{rooms.available}</strong>
                  </Link>
                  <Link to="/dashboard/rooms?tab=arrivals&filter=keys_pending" className={rooms.keys_pending ? 'is-alert' : ''}>
                    <span>Keys pending</span><strong>{rooms.keys_pending} guests</strong>
                  </Link>
                </div>
              </div>
              {guestsPendingAllocation > 0 && <footer><Hotel size={15} /> {guestsPendingAllocation} need rooms</footer>}
            </article>}
            {!eventTypeConfig.features.showAccommodation && !eventTypeConfig.features.showTravelAndId && (
              <PriorityCard priorities={visiblePriorities} compact />
            )}
          </section>

          {eventTypeConfig.features.showTravelAndId && <div className="qc-operations-grid">
            <section className="qc-transport">
              <div className="qc-transport__header">
                <span><Activity size={16} /> Guest transport desk</span>
                <StatusPill tone="live" dot>Live data</StatusPill>
              </div>
              <p className="qc-transport__eyebrow">Next: {formatTime(transport.next_arrival)}</p>
              <div className="flex items-center gap-1">
                <h2>Guest movement</h2>
                <InfoTip
                  content="Airport, station, and hotel transfer position."
                  label="About guest movement"
                  className="text-white/65 hover:bg-white/10 hover:text-white"
                />
              </div>
              <div className="qc-transport__stats">
                <span><small>Arrived</small><strong>{transport.arrived}</strong></span>
                <span><small>In transit</small><strong>{transport.in_transit}</strong></span>
                <span><small>Awaiting</small><strong>{transport.awaiting}</strong></span>
              </div>
              {transport.awaiting > 0 ? (
                <Link to="/dashboard/rooms?tab=arrivals" className="qc-transport__exception">
                  <TriangleAlert size={17} />
                  <span><strong>{transport.awaiting} awaiting pickup</strong></span>
                  <ArrowUpRight size={16} />
                </Link>
              ) : (
                <div className="qc-transport__clear">
                  <CheckCircle2 size={17} /> No unassigned guest pickups
                </div>
              )}
              <Link to="/dashboard/rooms?tab=arrivals" className="qc-button qc-button--light qc-button--full">
                <CarFront size={17} /> Open transport desk
              </Link>
            </section>

            <PriorityCard priorities={visiblePriorities} />
          </div>}
            </>
          )}
        </main>
      </div>

      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-xl p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Search the event workspace</DialogTitle>
            <DialogDescription>Open a dashboard module.</DialogDescription>
          </DialogHeader>
          <div className="qc-command-search">
            <Search size={18} />
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={eventTypeConfig.features.showAccommodation
                ? 'Search guests, rooms, tasks and tools'
                : 'Search guests, tasks and tools'}
              autoFocus
            />
          </div>
          <div className="qc-command-results">
            {searchResults.map(({ label, detail, to, icon: Icon }) => (
              <button
                type="button"
                key={to}
                onClick={() => {
                  setSearchOpen(false);
                  setSearchQuery('');
                  navigate(to);
                }}
              >
                <span><Icon size={18} /></span>
                <span><strong>{label}</strong><small>{detail}</small></span>
                <ArrowRight size={16} />
              </button>
            ))}
            {!searchResults.length && <p>No matching workspace tools.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xl gap-0 p-0">
          <form onSubmit={saveEvent} className="min-h-0">
            <DialogHeader className="px-4 pb-3 pt-4 sm:px-5 sm:pt-5 sm:before:-inset-x-5 sm:before:-top-5">
              <DialogTitle>Edit event details</DialogTitle>
              <DialogDescription>
                <span className="font-medium text-foreground">{draftEventTypeConfig.label}</span>
                <span aria-hidden="true"> &bull; </span>
                shown to guests/dashboard
              </DialogDescription>
            </DialogHeader>
            <div className="grid max-h-[calc(100dvh-9.5rem)] gap-4 overflow-y-auto px-4 py-4 sm:px-5">
              <section className="grid gap-3" aria-labelledby="qc-edit-identity-heading">
                <h3 id="qc-edit-identity-heading" className="text-sm font-semibold text-foreground">Identity</h3>
                {draftEventTypeConfig.id === 'wedding' && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="grid gap-1.5">
                      <Label htmlFor="qc-bride-name">Bride name *</Label>
                      <Input
                        id="qc-bride-name"
                        value={eventDraft.bride_name}
                        onChange={(event) => setEventDraft((current) => ({ ...current, bride_name: event.target.value }))}
                        required
                        data-testid="edit-event-bride-name"
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="qc-groom-name">Groom name *</Label>
                      <Input
                        id="qc-groom-name"
                        value={eventDraft.groom_name}
                        onChange={(event) => setEventDraft((current) => ({ ...current, groom_name: event.target.value }))}
                        required
                        data-testid="edit-event-groom-name"
                      />
                    </div>
                  </div>
                )}
                {draftEventTypeConfig.id === 'birthday' && (
                  <>
                    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_8rem]">
                      <div className="grid gap-1.5">
                        <Label htmlFor="qc-host-names">{draftEventTypeConfig.labels.hostNameLabel}</Label>
                        <Input
                          id="qc-host-names"
                          value={eventDraft.host_names}
                          onChange={(event) => setEventDraft((current) => ({ ...current, host_names: event.target.value }))}
                          placeholder="Separate names with commas"
                          data-testid="edit-event-host-names"
                        />
                      </div>
                      <div className="grid gap-1.5">
                        <Label htmlFor="qc-milestone-number">Turning</Label>
                        <Input
                          id="qc-milestone-number"
                          type="number"
                          min="1"
                          max="150"
                          value={eventDraft.milestone_number}
                          onChange={(event) => setEventDraft((current) => ({ ...current, milestone_number: event.target.value }))}
                          data-testid="edit-event-milestone-number"
                        />
                      </div>
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="qc-honoree-names">Celebrating *</Label>
                      <Input
                        id="qc-honoree-names"
                        value={eventDraft.honoree_names}
                        onChange={(event) => setEventDraft((current) => ({ ...current, honoree_names: event.target.value }))}
                        placeholder="Separate joint birthday names with commas"
                        required
                        data-testid="edit-event-honoree-names"
                      />
                    </div>
                  </>
                )}
                {draftEventTypeConfig.id !== 'wedding' && draftEventTypeConfig.id !== 'birthday' && (
                  <div className="grid gap-1.5">
                    <Label htmlFor="qc-host-names">{draftEventTypeConfig.labels.hostNameLabel}</Label>
                    <Input
                      id="qc-host-names"
                      value={eventDraft.host_names}
                      onChange={(event) => setEventDraft((current) => ({ ...current, host_names: event.target.value }))}
                      placeholder="Separate multiple names with commas"
                      data-testid="edit-event-host-names"
                    />
                  </div>
                )}
                <div className="grid gap-1.5">
                  <Label htmlFor="qc-event-name">Event name *</Label>
                  <Input
                    id="qc-event-name"
                    value={eventDraft.name}
                    onChange={(event) => setEventDraft((current) => ({ ...current, name: event.target.value }))}
                    data-testid="edit-event-name"
                    required
                  />
                </div>
              </section>

              <section className="grid gap-3" aria-labelledby="qc-edit-when-heading">
                <h3 id="qc-edit-when-heading" className="text-sm font-semibold text-foreground">When &amp; where</h3>
                <EventAwareDateRangePicker
                  id="qc-event-date-range"
                  label="Event dates"
                  startLabel="Start date"
                  endLabel="End date"
                  startValue={eventDraft.start_date}
                  endValue={eventDraft.end_date}
                  onStartChange={(date) => setEventDraft((current) => ({ ...current, start_date: date }))}
                  onEndChange={(date) => setEventDraft((current) => ({ ...current, end_date: date }))}
                  defaultToEventRange={false}
                  allowPastDates
                  required
                  data-testid="qc-event-date-range-input"
                />
              </section>

              {draftEventTypeConfig.id !== 'birthday' && (
                <section className="grid gap-3" aria-labelledby="qc-edit-notes-heading">
                  <h3 id="qc-edit-notes-heading" className="text-sm font-semibold text-foreground">Notes</h3>
                  <div className="grid gap-1.5">
                    <Label htmlFor="qc-event-description">Description</Label>
                    <Textarea
                      id="qc-event-description"
                      value={eventDraft.description}
                      onChange={(event) => setEventDraft((current) => ({ ...current, description: event.target.value }))}
                      rows={2}
                      data-testid="edit-event-description"
                    />
                  </div>
                </section>
              )}

              {draftEventTypeConfig.id === 'birthday' && (
                <section className="grid gap-3" aria-labelledby="qc-edit-venue-heading">
                  <h3 id="qc-edit-venue-heading" className="text-sm font-semibold text-foreground">Venue</h3>
                  <div className="rounded-xl border bg-background p-4">
                    <VenueSelector
                      value={eventDraft.venue || {}}
                      savedVenues={[]}
                      required={false}
                      onChange={(venue) => setEventDraft((current) => ({ ...current, venue }))}
                    />
                  </div>
                </section>
              )}
            </div>
            <DialogFooter className="px-4 pb-4 pt-3 sm:px-5 sm:pb-5 sm:before:-inset-x-5 sm:before:-bottom-5">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="sm:min-w-28">Cancel</Button>
              <Button type="submit" disabled={savingEvent} className="sm:min-w-32">{savingEvent ? 'Saving...' : 'Save changes'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
